/**
 * Amplify configuration for the staff portal.
 *
 * Per ADR-009:
 *   - User pool: nexa-staff-pool (separate from subscriber pool)
 *   - App client: PKCE flow, no client secret, no SSO
 *   - Token storage: sessionStorage under key `nexa_staff_token` (tab-scoped,
 *     cleared on tab close)
 *
 * Token storage rationale: localStorage persists across tab close, which
 * extends the window of an XSS-stolen token. sessionStorage clears on tab
 * close — strictly tighter for an internal admin tool.
 */
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { KeyValueStorageInterface } from 'aws-amplify/utils';

const env = import.meta.env;
const STAFF_TOKEN_STORAGE_KEY = 'nexa_staff_token';

/**
 * Wraps window.sessionStorage in the KeyValueStorageInterface that Amplify
 * expects. Namespaces keys under `nexa_staff_token:` so we never collide
 * with anything else in sessionStorage.
 */
function sessionStorageProvider(): KeyValueStorageInterface {
  const prefix = `${STAFF_TOKEN_STORAGE_KEY}:`;
  return {
    setItem: async (key: string, value: string): Promise<void> => {
      sessionStorage.setItem(prefix + key, value);
    },
    getItem: async (key: string): Promise<string | null> => {
      return sessionStorage.getItem(prefix + key);
    },
    removeItem: async (key: string): Promise<void> => {
      sessionStorage.removeItem(prefix + key);
    },
    clear: async (): Promise<void> => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(prefix)) keysToRemove.push(k);
      }
      for (const k of keysToRemove) sessionStorage.removeItem(k);
    },
  };
}

export function configureAmplify(): void {
  const userPoolId = env.VITE_STAFF_POOL_ID;
  const userPoolClientId = env.VITE_STAFF_POOL_CLIENT_ID;

  if (!userPoolId || !userPoolClientId) {
    // Fail loud in dev — silent failure here leaves the sign-in page broken
    // with no obvious cause.
    console.error(
      'portal-staff: missing VITE_STAFF_POOL_ID or VITE_STAFF_POOL_CLIENT_ID — copy .env.example to .env.local and populate from CDK outputs',
    );
  }

  // Pure user-pool config — PKCE sign-in goes through the SDK's signIn() API,
  // not Hosted UI, so no `loginWith.oauth` block is needed. Adding one would
  // switch Amplify's type union toward identity-pool config and require
  // identityPoolId, which we don't have.
  const config: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId: userPoolId ?? 'unconfigured',
        userPoolClientId: userPoolClientId ?? 'unconfigured',
        loginWith: {
          email: true,
        },
        mfa: {
          status: 'on',
          totpEnabled: true,
          smsEnabled: false,
        },
        userAttributes: { email: { required: true } },
        // NOTE: do not include `allowGuestAccess` here — Amplify v6's
        // AuthUserPoolConfig types it as `never`, and setting it shifts the
        // type union toward identity-pool config (which then requires
        // identityPoolId). Guest access is implicitly disabled by not having
        // an identity pool at all.
        passwordFormat: {
          minLength: 8,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: true,
        },
      },
    },
  };
  Amplify.configure(config);

  // Switch token storage to sessionStorage per ADR-009.
  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorageProvider());
}

export const ENV = {
  ADMIN_API_BASE_URL: env.VITE_ADMIN_API_BASE_URL,
  // Subscriber portal-api base (CDK output NexaPlatformStack.PortalApiBaseUrl).
  // The staff OBW fork (ADR-076 §9 copy-fork) provisions through portal-api's
  // self-authorising, HMAC-validated routes (POST /portal/bow/provision) —
  // exactly as the subscriber portal does — so the staff build injects the same
  // VITE_PORTAL_API_BASE_URL the subscriber build already uses. (deploy.yml
  // export rides with the proof-gated provisioning PR.)
  PORTAL_API_BASE_URL: env.VITE_PORTAL_API_BASE_URL,
  /**
   * ⛔ Turnstile site key (1 October 2026) — staff sign-in, verified by `nexa-staff-pre-auth` with
   * `nexa/turnstile-secret-portal` (the same widget as public sign-up). A key and its secret are a
   * PAIR; a mismatched pair rejects every real user. The partner application's POW key is gone
   * (2 October 2026): bot checks sit only at the doors, and that journey is inside one.
   * Cloudflare's always-pass TEST key is a DEV-SERVER fallback only. A production build without a
   * real key ships an empty string, the widget cannot render, and the form cannot submit —
   * the build itself refuses to ship that (deploy.yml guards), so a missing key is a failed
   * deploy, never a silently unprotected door.
   */
  TURNSTILE_SITE_KEY_PORTAL:
    (env.VITE_TURNSTILE_SITE_KEY_PORTAL as string | undefined) || (env.DEV ? '1x00000000000000000000AA' : ''),
  /**
   * ADR-124 D6/I8 — the sow-gate Worker's origin, for the PUBLIC per-zone gate read
   * (`GET /gate/beta/state?zone=<z>`) the BOW residency step makes before minting a participant
   * into a zone. UX only: `/admin/bow/launch` enforces the same gate server-side, fail-closed.
   * Defaults to the deployed Worker, exactly as the subscriber bundle's gate URLs do.
   */
  SOW_GATE_BASE_URL:
    (env.VITE_SOW_GATE_BASE_URL as string | undefined) || 'https://sow-gate.jeff-herbert.workers.dev',
};
